import { compareDatesWithSequenceIds } from "hos-tools";
import DutyStatusLog from "../../model/DutyStatusLog";
import type { DriveStore } from "../../store/reducers";
import type { IApiError } from "../api.interfaces";
import * as logger from "../logger";
import type { UserService } from "../users";

/**
 * hosStatusChange payload. `status`, `logId` and `dateTime` are all null when a
 * status subscribers may be holding is gone: the seated driver's current duty status
 * was removed, or a driver with no status took the seat from one that had one. They
 * can then drop the stale value. The shape never changes.
 */
export interface IHosStatusChangePayload {
    driverId: string;
    logId: string | null;
    status: string | null;
    dateTime: string | null;
}

/**
 * Latest standard duty status for a driver — the same definition
 * api.mobile.dutyStatusLog.getCurrentDrivingLog() uses
 * (HosProcess.getLatestLogForUser: standard-status driver logs ordered by
 * compareDatesWithSequenceIds, latest normal log). Two logs with the same
 * dateTime are ordered by their sequence, exactly as DutyStatusLogs.getSortedHosLogs does.
 */
export function getLatestHosStatus(driverId: string, logs: IIdDictionary<DutyStatusLog> | undefined): IHosStatusChangePayload | null {
    if (!driverId || !logs) {
        return null;
    }

    const latest = Object.keys(logs)
        .map(id => new DutyStatusLog(logs[id]))
        .filter(log => log.isStandardStatusDriverLog() && log.isNormalLogImpl())
        .sort(compareDatesWithSequenceIds)
        .pop();

    return latest ? { driverId, logId: latest.id, status: latest.status, dateTime: latest.dateTime } : null;
}

/**
 * Producer for api.mobile.on("hosStatusChange") (MYG-148936).
 *
 * One app-wide subscription (the launcher owns it), not one per user context,
 * so each status change is delivered once. It fires when the driver-seat
 * user's current duty status changes — including when a different driver takes
 * the seat (the payload always carries driverId) and when a status that was
 * announced clears (null fields). Edits that keep the same status do not fire.
 * An empty seat (last-driver logout) does not fire: that is driverLogout
 * (MYG-148937), not a duty-status change.
 *
 * "No status yet" is not news: while nothing has been announced in this session
 * (app start, a first login) a seated driver without a current status does not
 * fire. A first login writes a Login log, which is not a duty status, before the
 * HOS fetch lands; announcing that would send a null and then the real status.
 * The first status is therefore the first event, and there is no event for the
 * baseline, so add-ins read getCurrentDrivingLog() for the starting value.
 * Once a status has been announced, the null is meaningful (subscribers may still
 * hold it) and does fire, including when the next driver has none: an empty seat
 * never resets what was announced, because a seat handoff on logout passes
 * through one.
 *
 * A seated driver whose log slice is missing from the store is "not loaded",
 * not "no status": Session.clearStore drops a logging-out user's slice before
 * it removes the user, and a new login is seated before its first HOS sync.
 * Neither fires; the cleared payload is for a slice that exists but holds no
 * current status.
 *
 * Resolving the seat user deep-clones the users dictionary, so that work only
 * runs when one of the store slices it reads changes reference; ordinary Redux
 * dispatches (GPS, UI) cost a few reference compares. Does nothing outside the
 * DriveNative host, where the event channel isn't injected. Nothing here may
 * throw into Redux dispatch or into app startup: malformed logs (in the store at
 * start, or arriving later) are reported once and skipped, and the subscription
 * keeps running so the next valid status is announced.
 */
export function startHosStatusChangeEvents(store: DriveStore, userService: UserService): (() => void) | undefined {
    if (typeof window.__rnDispatchAddinEvent !== "function") {
        return undefined;
    }

    // Every slice getDriversSeatUser() and the HOS log lookup read.
    const sources = () => {
        const state = store.getState();
        return [state.driverSeatUser, state.activeUsers, state.users, state.userHosLogs];
    };
    const snapshot = () => {
        const driverId = userService.getDriversSeatUser()?.id || "";
        const logs = driverId ? store.getState().userHosLogs[driverId] as IIdDictionary<DutyStatusLog> | undefined : undefined;
        return { driverId, logs };
    };
    // Seated driver → their current status, or a "cleared" payload; empty seat → null.
    const currentPayload = (driverId: string, logs: IIdDictionary<DutyStatusLog> | undefined): IHosStatusChangePayload | null => {
        if (!driverId) {
            return null;
        }
        return getLatestHosStatus(driverId, logs) ?? { driverId, logId: null, status: null, dateTime: null };
    };
    const keyOf = (payload: IHosStatusChangePayload | null) => payload ? `${ payload.driverId }|${ payload.status ?? "" }` : "";

    // Report once: this runs on every change to the slices above, so a bad log would repeat.
    let hasReportedFailure = false;
    const reportFailure = (error: unknown) => {
        if (!hasReportedFailure) {
            hasReportedFailure = true;
            logger.error(logger.Source.ADDIN, `hosStatusChange producer failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
        }
    };

    // The baseline is read from whatever is already in the store (userHosLogs is preloaded from storage),
    // so it can hit the same malformed data the subscription guards against. This is an optional feature
    // called from app startup: on failure start from an unknown baseline ("") instead of throwing, so the
    // subscription below still attaches and the next valid status is announced.
    let lastSources: unknown[] = [];
    let last: ReturnType<typeof snapshot> = { driverId: "", logs: undefined };
    let lastKey = "";
    try {
        lastSources = sources();
        last = snapshot();
        lastKey = keyOf(currentPayload(last.driverId, last.logs));
    } catch (error) {
        reportFailure(error);
    }

    return store.subscribe(() => {
        try {
            const nextSources = sources();
            if (nextSources.every((source, index) => source === lastSources[index])) {
                return;
            }
            lastSources = nextSources;

            const next = snapshot();
            if (next.driverId === last.driverId && next.logs === last.logs) {
                return;
            }
            last = next;
            if (next.driverId && next.logs === undefined) {
                return;
            }

            const payload = currentPayload(next.driverId, next.logs);
            if (!payload) {
                // Empty seat: not announced here (that is driverLogout, MYG-148937). Keep lastKey, because a
                // seat handoff passes through an empty seat: removeActiveUser drops the departing driver from
                // the active list (the seat then resolves to nobody) before it moves the seat to the next one.
                // Forgetting here would hide that the old driver's status is gone.
                return;
            }
            const key = keyOf(payload);
            if (key === lastKey) {
                return;
            }
            // Nothing announced yet (app start with nobody seated, or a first login) and the seated driver has
            // no status yet: that is "not known", not "cleared". A first login writes a Login log, which is not
            // a duty status, before the HOS fetch lands, so announcing now would send null and then the real
            // status. Leave lastKey alone: the status, when it arrives, is the first thing subscribers hear.
            if (payload.status === null && lastKey === "") {
                return;
            }
            lastKey = key;

            window.__rnDispatchAddinEvent?.("hosStatusChange", payload);
        } catch (error) {
            // The bridge isolates handler errors, and a malformed log must not break Redux dispatch.
            reportFailure(error);
        }
    });
}

/** themeChange payload: the same shape api.mobile.theme.get() resolves to. */
export interface IThemeChangePayload {
    nightMode: boolean;
}

/**
 * The theme Drive renders add-ins on. api.mobile.theme.get() and api.mobile.on("themeChange") both
 * come from this one function, so the read and the event can never disagree.
 *
 * Both the native Dark Mode toggle (via SET_NIGHTMODE) and Drive's own settings toggle write
 * data.nightMode, so this is correct on every host. Mirrors the rule in AppView.handleNightMode: the
 * Lytx whitelabel is always dark. The override there that drops night mode while an inspection page is
 * open is left out: it is transient page state (the inspection container or route), not a theme, and an
 * add-in page isn't the one on screen while it applies.
 *
 * nightMode is coerced so the public contract stays a boolean even if the store shape drifts.
 */
export function getAddinTheme(store: DriveStore): IThemeChangePayload {
    return { nightMode: window.whitelabel === "lytx" || !!store.getState().data.nightMode };
}

/**
 * Producer for api.mobile.on("themeChange") (MYG-150290).
 *
 * Fires with { nightMode } each time the theme add-ins are rendered on actually changes, so an add-in
 * that follows Drive's night mode does not have to poll theme.get() or watch #app.nightMode. The
 * starting theme is a baseline, not news: there is no event for it, and add-ins read theme.get() for
 * it. A dispatch that leaves the theme as it was (the other toggle written to the same value, the
 * Lytx whitelabel that is always dark) fires nothing.
 *
 * The theme is device-wide, so each change is delivered once.
 *
 * Only starts when the DriveNative host advertises the event in __rnGetAddinEvents. An older host
 * does not know the name and would log a warning for every change.
 *
 * Nothing here may throw into Redux dispatch or app startup.
 */
export function startThemeChangeEvents(store: DriveStore): (() => void) | undefined {
    if (typeof window.__rnDispatchAddinEvent !== "function") {
        return undefined;
    }
    let supported = false;
    try {
        supported = (window.__rnGetAddinEvents?.() ?? []).indexOf("themeChange") !== -1;
    } catch {
        supported = false;
    }
    if (!supported) {
        return undefined;
    }

    // Report once: this runs on every store change, so a persistent failure would repeat.
    let hasReportedFailure = false;
    const reportFailure = (error: unknown) => {
        if (!hasReportedFailure) {
            hasReportedFailure = true;
            logger.error(logger.Source.ADDIN, `themeChange producer failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
        }
    };

    // What subscribers were last told; starts as the theme found at start, which is not announced.
    let lastNightMode: boolean | undefined;
    try {
        lastNightMode = getAddinTheme(store).nightMode;
    } catch (error) {
        reportFailure(error);
    }

    return store.subscribe(() => {
        try {
            const theme = getAddinTheme(store);
            if (theme.nightMode === lastNightMode) {
                return;
            }
            lastNightMode = theme.nightMode;
            window.__rnDispatchAddinEvent?.("themeChange", theme);
        } catch (error) {
            // The bridge isolates handler errors, and a bad store read must not break Redux dispatch.
            reportFailure(error);
        }
    });
}

/** driverLogin / driverLogout / driverSeatChange payload. */
export interface IDriverEventPayload {
    driverId: string;
}

/** vehicleChange payload. vehicleId is null when there is no current vehicle. */
export interface IVehicleChangePayload {
    vehicleId: string | null;
}

/**
 * Drive's own "no vehicle" value (the driver chose to continue without one). To an add-in that is
 * simply no vehicle, so it is reported as null.
 */
const NO_VEHICLE_ID = "NoDeviceId";

/** True when the host lists every one of these events in __rnGetAddinEvents. */
function hostListsEvents(names: string[]): boolean {
    if (typeof window.__rnDispatchAddinEvent !== "function") {
        return false;
    }
    try {
        const listed = window.__rnGetAddinEvents?.() ?? [];
        return names.every(name => listed.indexOf(name) !== -1);
    } catch {
        return false;
    }
}

/** Ids of the drivers logged in, in login order. A driver without an id cannot be named and is skipped. */
function loggedInDriverIds(state: ReturnType<DriveStore["getState"]>): string[] {
    const users = state.users as IIdDictionary<{ id?: string }> | undefined;
    const ids: string[] = [];
    (state.activeUsers as readonly string[]).forEach(name => {
        const id = users?.[name]?.id;
        if (id && ids.indexOf(id) === -1) {
            ids.push(id);
        }
    });
    return ids;
}

/**
 * Producer for api.mobile.on("driverLogin" | "driverLogout" | "driverSeatChange") (MYG-148937).
 *
 * Replaces add-ins reading localStorage to find out who is logged in. Drivers are identified by id, the
 * same id hosStatusChange, user.get() and a duty log's coDrivers use.
 *
 * - driverLogin: a driver joins the active users. driverLogout: a driver leaves them.
 * - driverSeatChange: a different driver takes the driver's seat. A first login that seats the only
 *   driver counts.
 * - An empty seat is not announced; the seated driver's driverLogout already says the seat is vacated.
 *   When that driver logs out while another is still logged in, the store passes through an empty seat
 *   before the next driver is seated, so the seat change is announced once, for the driver who ends up
 *   seated. The seat starts over each time the seated driver logs out: whoever is seated next, even the
 *   same driver logging back in, is a seat change.
 * - One event per real change. In one store update logouts come first, then logins, then the seat.
 *   An unrelated dispatch, or the same driver in the same seat again, fires nothing.
 * - There is no event for the starting state: who is logged in and seated at start is a baseline.
 *   Add-ins read user.get() for it.
 * - Device-wide, not per user, so each change is delivered once.
 *
 * Reads the store slices directly (active user names, the users dictionary, the seat name) and only
 * does work when one of those slices changes reference, so ordinary dispatches (GPS, UI) cost a few
 * reference compares. Only starts when the DriveNative host lists all three events. Nothing here may
 * throw into Redux dispatch or app startup; a failing dispatch of one event does not stop the others.
 */
export function startDriverEvents(store: DriveStore): (() => void) | undefined {
    if (!hostListsEvents(["driverLogin", "driverLogout", "driverSeatChange"])) {
        return undefined;
    }

    // Report once: this runs on every change to the slices below, so a persistent failure would repeat.
    let hasReportedFailure = false;
    const reportFailure = (error: unknown) => {
        if (!hasReportedFailure) {
            hasReportedFailure = true;
            logger.error(logger.Source.ADDIN, `driver events producer failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
        }
    };

    const sources = () => {
        const state = store.getState();
        return [state.activeUsers, state.users, state.driverSeatUser];
    };
    // Logged-in drivers that have an id (in login order), and the seated driver's id or "" when the seat
    // is empty or its holder is not logged in.
    const read = () => {
        const state = store.getState();
        const users = state.users as IIdDictionary<{ id?: string }> | undefined;
        const activeNames = state.activeUsers as readonly string[];
        const ids = loggedInDriverIds(state);
        const seatName = state.driverSeatUser;
        const seatId = seatName && activeNames.indexOf(seatName) !== -1 ? (users?.[seatName]?.id ?? "") : "";
        return { ids, seatId };
    };
    const emit = (name: string, driverId: string) => {
        try {
            window.__rnDispatchAddinEvent?.(name, { driverId } as IDriverEventPayload);
        } catch (error) {
            // The bridge isolates handler errors; a failing channel must not break Redux dispatch or the other events.
            reportFailure(error);
        }
    };

    // The baseline is read from whatever is already in the store, so it can hit malformed data the
    // subscription guards against. On failure start from nobody logged in and attach anyway.
    let lastSources: unknown[] = [];
    let lastIds: string[] = [];
    let lastSeatId = "";
    try {
        lastSources = sources();
        const baseline = read();
        lastIds = baseline.ids;
        lastSeatId = baseline.seatId;
    } catch (error) {
        reportFailure(error);
    }

    return store.subscribe(() => {
        try {
            const nextSources = sources();
            if (nextSources.every((source, index) => source === lastSources[index])) {
                return;
            }
            lastSources = nextSources;

            const next = read();
            const loggedOut = lastIds.filter(id => next.ids.indexOf(id) === -1);
            const loggedIn = next.ids.filter(id => lastIds.indexOf(id) === -1);
            // The seated driver logging out vacates the seat: subscribers were told driverLogout, so they
            // know it is empty, and whoever is seated next (even the same driver back) is a seat change.
            const seatBefore = next.ids.indexOf(lastSeatId) === -1 ? "" : lastSeatId;
            const seated = next.seatId !== "" && next.seatId !== seatBefore ? next.seatId : "";

            // State first, so a failing dispatch never repeats the same change.
            lastIds = next.ids;
            lastSeatId = seated || seatBefore;

            loggedOut.forEach(id => emit("driverLogout", id));
            loggedIn.forEach(id => emit("driverLogin", id));
            if (seated) {
                emit("driverSeatChange", seated);
            }
        } catch (error) {
            reportFailure(error);
        }
    });
}

/**
 * Tells subscribers that the drivers still logged in are leaving, for the logout that ends the session.
 *
 * The producer above emits driverLogout when a driver leaves the active users. On the last driver's logout
 * that is the very end, after the registrations are cleared (releaseAllAddinEvents), so no add-in would hear
 * it. Call this just before that clear. It delivers synchronously, so handlers have run by the time the
 * registrations go.
 *
 * Drivers are identified by id, as in the producer. The producer still emits when the driver is removed; by
 * then there are no registrations to reach. Nothing here may throw: it runs inside logout.
 */
export function announceLoggedInDriversLeaving(store: DriveStore): void {
    if (!hostListsEvents(["driverLogin", "driverLogout", "driverSeatChange"])) {
        return;
    }
    try {
        loggedInDriverIds(store.getState()).forEach(driverId => {
            try {
                window.__rnDispatchAddinEvent?.("driverLogout", { driverId } as IDriverEventPayload);
            } catch (error) {
                logger.error(logger.Source.ADDIN, `driverLogout on logout failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
            }
        });
    } catch (error) {
        logger.error(logger.Source.ADDIN, `driverLogout on logout failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
    }
}

/**
 * Producer for api.mobile.on("vehicleChange") (MYG-148937).
 *
 * Fires with { vehicleId } when the current vehicle changes: a vehicle is selected or replaced, or it
 * is cleared (vehicleId null) so an add-in holding the old id can let go of it. Drive's "no vehicle"
 * value is reported as null. The current vehicle is device-wide, so each change is delivered once.
 *
 * There is no event for the starting vehicle; add-ins read vehicle.get() for it. Selecting the vehicle
 * that is already current fires nothing.
 *
 * Only starts when the DriveNative host lists the event. Nothing here may throw into Redux dispatch
 * or app startup.
 */
export function startVehicleChangeEvents(store: DriveStore): (() => void) | undefined {
    if (!hostListsEvents(["vehicleChange"])) {
        return undefined;
    }

    let hasReportedFailure = false;
    const reportFailure = (error: unknown) => {
        if (!hasReportedFailure) {
            hasReportedFailure = true;
            logger.error(logger.Source.ADDIN, `vehicleChange producer failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
        }
    };
    const vehicleIdOf = (): string | null => {
        const id = store.getState().vehicleHistory.currentVehicle?.id;
        return id && id !== NO_VEHICLE_ID ? id : null;
    };

    let lastVehicleId: string | null = null;
    try {
        lastVehicleId = vehicleIdOf();
    } catch (error) {
        reportFailure(error);
    }

    return store.subscribe(() => {
        try {
            const vehicleId = vehicleIdOf();
            if (vehicleId === lastVehicleId) {
                return;
            }
            lastVehicleId = vehicleId;
            window.__rnDispatchAddinEvent?.("vehicleChange", { vehicleId } as IVehicleChangePayload);
        } catch (error) {
            // The bridge isolates handler errors, and a bad store read must not break Redux dispatch.
            reportFailure(error);
        }
    });
}

/** drivingChange payload. */
export interface IDrivingChangePayload {
    isDriving: boolean;
}

// Last true/false seen per store from the source that owns the value now. Shared by the producer and
// api.mobile.driving.get(); the producer forgets it whenever the host takes over or hands back the lock screen.
const lastKnownDriving = new WeakMap<DriveStore, boolean>();

/**
 * The driving value add-ins are told, as true, false, or undefined when the app does not know.
 *
 * While the DriveNative movement service owns the lock screen, the host pushes its motion state into
 * data.nativeMotion and that is the value here, so the event, driving.get() and the lock screen the driver
 * sees agree. Otherwise it is the web app's own data.driving. Once the host owns it the web value is never
 * used as a fallback: the web app keeps computing it under its own rules, and it can disagree with the
 * native lock screen.
 */
export function readDriving(store: DriveStore): boolean | undefined {
    const { nativeMotion, driving } = store.getState().data;
    switch (nativeMotion) {
        case undefined:
            return driving;
        case "moving":
            return true;
        case "stopped":
            return false;
        default:
            return undefined;
    }
}

/**
 * The last true/false the source that owns the driving value now (the web app, or the native movement
 * service once the host owns the lock screen) held since the producer started or since the owner last
 * changed, or undefined if that source has not known. api.mobile.driving.get() falls back to it while the
 * live value is unknown. A value from the other source is never a fallback: the web app can say driving
 * while the native lock screen is unlocked.
 */
export function getLastKnownDriving(store: DriveStore): boolean | undefined {
    return lastKnownDriving.get(store);
}

/**
 * Producer for api.mobile.on("drivingChange") (MYG-148950), and the owner of the last-known driving value
 * that api.mobile.driving.get() reads.
 *
 * Replaces add-ins watching the #lockscreen-panel element to find out whether the driver is driving: an
 * add-in is told the driving state itself, not the lock screen. There is deliberately no way for an
 * add-in to read or dismiss the lock screen.
 *
 * The source is readDriving(): state.data.driving, the same value that shows and hides the web lock screen, or the
 * native movement service's state once the host owns the lock screen. It is true, false, or undefined when
 * the app does not know (for example after GPS silence). Unknown is not
 * news: the lock screen keeps its current state in that case, and so does this event, so only a real
 * true/false change is announced. There is no event for the starting state, and nothing is announced
 * again when the value returns to what subscribers were last told. The exception is a change of owner
 * (the host takes over the lock screen or hands it back) before the new owner knows: get() then answers
 * null, so the new owner's first true/false is announced even if it matches the last event.
 *
 * The last true/false is kept even when unknown follows, so an add-in that starts during GPS silence can
 * still read it with driving.get(): it would never be re-announced. It is kept per owner: when the host takes
 * over the lock screen or hands it back, the previous owner's value is forgotten until the new one knows.
 *
 * The value is device-wide, not per user, so each change is delivered once.
 *
 * It always tracks, because driving.get() is exposed on every host. It only announces when the DriveNative
 * host advertises the event in __rnGetAddinEvents: an older host does not know the name and would log a
 * warning for every change.
 *
 * Nothing here may throw into Redux dispatch or app startup.
 */
export function startDrivingChangeEvents(store: DriveStore): () => void {
    const canAnnounce = hostListsEvents(["drivingChange"]);

    // Report once: this runs on every store change, so a persistent failure would repeat.
    let hasReportedFailure = false;
    const reportFailure = (error: unknown) => {
        if (!hasReportedFailure) {
            hasReportedFailure = true;
            logger.error(logger.Source.ADDIN, `drivingChange producer failed: ${ (error as Error)?.message ?? error }`, undefined, error as IApiError);
        }
    };

    const isNativeOwned = () => store.getState().data.nativeMotion !== undefined;

    // What subscribers were last told. It survives a change of owner that lands straight on a true/false, so that
    // value is not announced again. It is dropped when the new owner does not know yet, because an add-in that
    // starts then reads null from get() and must be told the new owner's first value, even if it matches.
    let lastTold: boolean | undefined;
    let wasNativeOwned = false;

    // The value found at start is a baseline, not news, so it is recorded but not announced.
    try {
        wasNativeOwned = isNativeOwned();
        const driving = readDriving(store);
        if (typeof driving === "boolean") {
            lastKnownDriving.set(store, driving);
            lastTold = driving;
        }
    } catch (error) {
        reportFailure(error);
    }

    return store.subscribe(() => {
        try {
            if (isNativeOwned() !== wasNativeOwned) {
                wasNativeOwned = !wasNativeOwned;
                lastKnownDriving.delete(store);
                if (typeof readDriving(store) !== "boolean") {
                    lastTold = undefined;
                }
            }
            const driving = readDriving(store);
            // Unknown or not a boolean, or what subscribers already have: no news.
            if (typeof driving !== "boolean") {
                return;
            }
            lastKnownDriving.set(store, driving);
            if (driving === lastTold) {
                return;
            }
            lastTold = driving;
            if (!canAnnounce) {
                return;
            }

            const payload: IDrivingChangePayload = { isDriving: driving };
            window.__rnDispatchAddinEvent?.("drivingChange", payload);
        } catch (error) {
            // The bridge isolates handler errors, and a bad store read must not break Redux dispatch.
            reportFailure(error);
        }
    });
}
